import {Field, Formik, FormikHelpers as FormikActions} from 'formik';
import {default as React} from 'react';
import {Button} from '~/components/Button';
import {Form, FormColumn, FormControls, FormRow, FormTitle, InputField, IntentText, CheckboxField, FormField, hasError, IFieldProps} from '~/components/Form';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {Intent} from '~/lib/intent';
import {ID} from '~/lib/model';
import {IDeal, DealSchema} from '~/modules/Pmp/models/Deal';
import {ISelectProps} from '~/components/Controls/Select';
import {SelectAgency} from '~/modules/Clients/components/SelectAgency';
import {MultiSelectBidderField} from '~/modules/Bidders/components/MultiSelectBidder';
import {SelectSupplyPackageField} from '~/modules/Pmp/components/SelectSupplyPackage';


interface IDealForm {
    deal?: IDeal;
    onSubmit?: (values: IDeal, formikActions: FormikActions<IDeal>, close: IModalComponentClose) => void;
}

const normalizeId = (value: any): number | null => {
    if (typeof value === 'number') return value;
    if (value && typeof value === 'object' && 'value' in value) {
        const numeric = Number(value.value);
        return Number.isNaN(numeric) ? null : numeric;
    }
    return null;
};

const normalizeIdArray = (values: any): number[] => {
    if (!Array.isArray(values)) return [];
    return values
        .map(item => normalizeId(item))
        .filter((id): id is number => typeof id === 'number');
};

type ISelectAgencyProps = Omit<ISelectProps<ID>, 'items'>;

const SelectAgencyWithBidderResetField = (props: IFieldProps<ISelectAgencyProps>) => <FormField {...props}>
    <SelectAgency {...props.options}
        intent={hasError(props) && Intent.DANGER}
        value={props.field.value} onChange={(value) => {
            if (value !== props.field.value) {
                props.form.setFieldValue('bidderIds', []);
            }
            props.form.setFieldValue(props.field.name, value);
        }}
    />
</FormField>;

export const DealForm: IModalComponent<IDealForm> = (props) => {
    const initialValues = React.useMemo<IDeal | undefined>(() => {
        if (!props.deal) {
            return props.deal;
        }
        return {
            ...props.deal,
            bidderIds: normalizeIdArray(props.deal.bidderIds),
            supplyPackageId: normalizeId(props.deal.supplyPackageId) ?? 0
        };
    }, [props.deal]);

    return <Formik initialValues={initialValues}
        validationSchema={DealSchema}
        onSubmit={(deal, actions) => props.onSubmit?.({
            ...deal,
            supplyPackageId: normalizeId(deal.supplyPackageId) ?? 0,
        }, actions, props.close)}
    >
    {({values: deal, status, isSubmitting}) => <Form>
        <FormTitle>{deal.id ? 'Edit Deal' : 'Create Deal'}</FormTitle>
        {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
        <FormRow>
            <FormColumn>
                <Field name='id'
                    options={{
                        placeholder: 'Deal id',
                        disabled: isSubmitting
                    }}
                    label='ID'
                    component={InputField}
                />
                <Field name='name'
                    options={{
                        placeholder: 'Deal name',
                        disabled: isSubmitting
                    }}
                    label='Name'
                    component={InputField}
                />
                <Field name='agencyId'
                    label='Agency'
                    options={{
                        placeholder: 'Agency',
                        searchable: true,
                        disabled: isSubmitting
                    }}
                    component={SelectAgencyWithBidderResetField}
                />
                <Field name='bidderIds'
                    label='Bidders'
                    options={{
                        searchable: true,
                        placeholder: 'Bidders',
                        agencyId: deal.agencyId,
                        disabled: isSubmitting || !deal.agencyId
                    }}
                    component={MultiSelectBidderField}
                />
                <Field name='supplyPackageId'
                    label='Supply Package'
                    options={{
                        placeholder: 'Supply Package',
                        searchable: true,
                        disabled: isSubmitting
                    }}
                    component={SelectSupplyPackageField}
                />
                <Field name='isActive'
                    label='Active'
                    options={{
                        placeholder: 'Active',
                        disabled: isSubmitting
                    }}
                    component={CheckboxField}
                />
                <Field name='privateAuction'
                    label='Private auction'
                    options={{
                        placeholder: 'privateAuction',
                        disabled: isSubmitting
                    }}
                    component={CheckboxField}
                />
                <Field name='bidfloor'
                    label='Bidfloor'
                    options={{
                        placeholder: 'Bidfloor',
                        type: 'number',
                        step: 0.01,
                        min: 0,
                        disabled: isSubmitting
                    }}
                    component={InputField}
                />
                <Field name='bidfloorCur'
                    options={{
                        placeholder: 'Bidfloor currency',
                        disabled: true
                    }}
                    label='Bidfloor currency'
                    component={InputField}
                />
            </FormColumn>
        </FormRow>
        <FormControls>
            <Button disabled={isSubmitting}
                onClick={props.close}
            >Cancel</Button>
            <Button type='submit'
                disabled={isSubmitting}
                intent={Intent.PRIMARY}
                rightIcon={isSubmitting && <i className='fa fa-spinner fa-spin'/>}
            >Save</Button>
        </FormControls>
    </Form>}
</Formik>;
};
